import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withKey, API_BASE } from '../services/utils';

// Zoho CRM "Invoices to Generate" create form (CustomModule4).
const ZOHO_CREATE_BASE = 'https://crm.zoho.eu/crm/80days/tab/CustomModule4/create';
const ZOHO_LAYOUT_ID = '869988000001766763';

// Zoho needs a moment to settle after the offer is written before the create
// form reads it back, so we hold the loading screen a little longer.
const POST_SAVE_DELAY_MS = 5000;

function buildZohoUrl(offerId) {
  const params = new URLSearchParams({
    layoutId: ZOHO_LAYOUT_ID,
    redirect: 'false',
    offer_id: offerId,
  });
  return `${ZOHO_CREATE_BASE}?${params.toString()}`;
}

/**
 * Landing page for /generate_invoices?offer_id=<zoho_offer_id>&key=<SECURITY_KEY>
 *
 * 1. Saves the offer to Zoho (same server-side path as the disk-icon button).
 * 2. Waits POST_SAVE_DELAY_MS.
 * 3. Redirects to the Zoho invoice-generation form for that offer.
 *
 * On failure it stops and shows the error — the user is never forwarded with
 * potentially stale figures.
 */
function GenerateInvoices() {
  const [phase, setPhase] = useState('saving'); // 'saving' | 'waiting' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [attempt, setAttempt] = useState(0);

  const startedRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const params = new URLSearchParams(window.location.search);
  const offerId = params.get('offer_id');
  const hasKey = !!params.get('key');

  const run = useCallback(async () => {
    if (!offerId) {
      setPhase('error');
      setErrorMessage('Missing offer_id in the URL.');
      return;
    }
    if (!hasKey) {
      setPhase('error');
      setErrorMessage('Missing access key in the URL.');
      return;
    }

    setPhase('saving');
    setErrorMessage('');

    try {
      const url = await withKey(`${API_BASE}/api/zoho/offers/by-zoho-id/${encodeURIComponent(offerId)}/sync`);
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });

      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }

      if (!response.ok || (data && data.error)) {
        const detail = (data && data.error) || `HTTP ${response.status}`;
        setPhase('error');
        setErrorMessage(detail);
        return;
      }

      setPhase('waiting');
      redirectTimerRef.current = setTimeout(() => {
        // replace() keeps the loading page out of the history stack, so the
        // browser back button does not re-trigger the save.
        window.location.replace(buildZohoUrl(offerId));
      }, POST_SAVE_DELAY_MS);
    } catch (error) {
      setPhase('error');
      setErrorMessage(error?.message || 'Could not reach the server.');
    }
  }, [offerId, hasKey]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    run();
  }, [run]);

  // Re-run on "Try again".
  useEffect(() => {
    if (attempt === 0) return;
    run();
  }, [attempt, run]);

  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
  }, []);

  if (phase === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-company-toned px-4">
        <div className="w-full max-w-md rounded-lg border border-black/10 bg-white p-8 text-center shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-black/80">
            The offer could not be saved
          </h1>
          <p className="mb-1 break-words text-sm text-black/60">{errorMessage}</p>
          {offerId && (
            <p className="mb-6 text-xs text-black/40">Offer ID: {offerId}</p>
          )}
          <button
            type="button"
            onClick={() => setAttempt(a => a + 1)}
            className="rounded bg-company-main px-4 py-2 text-sm font-medium text-white transition hover:bg-company-main/90"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-company-toned px-4">
      <div className="text-center">
        <div
          className="mx-auto mb-6 h-10 w-10 animate-spin rounded-full border-4 border-company-main/25 border-t-company-main"
          role="status"
          aria-label="Loading"
        />
        <p className="text-sm font-medium text-black/70">
          {phase === 'saving'
            ? 'Saving the offer to Zoho...'
            : 'Offer saved. Opening the invoice form...'}
        </p>
        <p className="mt-1 text-xs text-black/40">Please do not close this window.</p>
      </div>
    </div>
  );
}

export default GenerateInvoices;
